<script lang="ts">
  import type { FacetSlot, FilterFormProps } from "./FilterFormTypes";

  const {
    filterId,
    filterTitle,
    ruleString,
    sortKey,
    forceIndex,
    hidden,
    aggregated,
    switchable,
    selected,
    hasNumericRange,
    nextBestFilterButtons,
    facetGroupLabel,
    facetGroupsJson,
    formFeatureTracking,
    deselect,
    facetGroups,
    position = 0,
  }: FilterFormProps = $props();

  const formId = $derived(`find_filter_${formFeatureTracking.id}`);

  function dismissBanner(event: Event) {
    (event.currentTarget as HTMLElement).dispatchEvent(
      new CustomEvent("heureka.filterMessageBanner.dismiss", {
        detail: { filterId },
        bubbles: true,
      }),
    );
  }
</script>

{#snippet facetSlot(facet: FacetSlot)}
  <div class="hc_svelteFacetWrapper">
    <span
      class="ts_heureka_svelteFacet"
      data-filter-id={facet.filterId}
      data-facet-id={facet.facetId}
      data-selection-type={facet.selectionType}
      data-facet-visibility={facet.facetVisibility}
      data-filter-title-mode={facet.filterTitleMode}
      data-has-nba-values={facet.hasNbaValues ? "true" : "false"}
      data-serialized={facet.serialized}
      {...facet.intervalFilter ? { "data-may-hide-slider": facet.mayHideSlider ? "true" : "false" } : {}}
      data-show-title={facet.showTitle ? "true" : "false"}
    ></span>
  </div>
{/snippet}

<form
  id={formId}
  data-ts-feature-name={formFeatureTracking.name}
  data-ts-feature-parent-id="find_filterSection"
  {...position > 0 ? { "data-ts-feature-position": String(position) } : {}}
  data-ts-feature-status={formFeatureTracking.status}
  data-ts-feature-labels={formFeatureTracking.labelsJson}
  class={`find_filter${switchable ? " find_filter--switchable" : ""} js_trackOnChange ts_formFilter`}
  data-title={filterTitle}
  {...forceIndex >= 0 ? { "data-force-index": String(forceIndex) } : {}}
  data-ts-change-labels={JSON.stringify({ san_FacetLocation: "sidebar" })}
  action="/grasshopper/filter/resolve"
  autocomplete="off"
  method="post"
  novalidate
  {hidden}
>
  <oc-banner-v1 class="find_filterMessage" variant="warning" hide-icon hidden onclick={dismissBanner}>
    <span class="js_filterMessageText"></span>
  </oc-banner-v1>

  {#if switchable}
    <div class="find_filter__switch">
      <div
        class="ts_heureka_svelteFacetGroupDropdown"
        data-serialized={facetGroupsJson}
        data-label={facetGroupLabel}
        data-name="group"
        data-filter-id={filterId}
      ></div>
    </div>
  {/if}

  <input type="hidden" name="rule" value={ruleString} />
  <input type="hidden" name="sortiertnach" value={sortKey} />

  {#if aggregated}
    <ul>
      {#each facetGroups as group (group.name)}
        <li class={`find_aggregatedFacet find_aggregatedFacet--${group.name}`} hidden={group.hidden}>
          {#each group.facets as facet (facet.facetId)}
            {@render facetSlot(facet)}
          {/each}
        </li>
      {/each}
    </ul>
  {:else}
    {#each facetGroups as group (group.name)}
      {#each group.facets as facet (facet.facetId)}
        {@render facetSlot(facet)}
      {/each}
    {/each}
  {/if}

  <footer class="find_filter__footer">
    {#if hasNumericRange}
      <oc-button-v1
        variant="primary"
        size="50"
        class="blockOnLarge ts_heureka_selectRange find_filter__select find_filter__select--range find_filter__select--hidden"
        type="submit">Auswahl ansehen</oc-button-v1
      >
    {/if}
    {#if deselect.show}
      <div class="find_filter__deselect-wrapper">
        <oc-link-v2
          data-ts-feature-labels={deselect.featureTracking.labelsJson}
          variant="secondary"
          size="50"
          class="find_filter__deselect ts_deselect-filter hideInSheet"
          data-form={`find_filter_${deselect.featureTracking.id}`}
          as-button>Aufheben</oc-link-v2
        >
      </div>
    {/if}
    <oc-button-v1
      class="heureka_menu-bar-button hideOnLarge find_filter__select js_closeFilterSheet js_trackFilterMethod"
      variant="secondary"
      form={formId}
      type="button"
      oc-aria-label="Fertig">Fertig</oc-button-v1
    >
    <oc-button-v1
      class="heureka_menu-bar-button hideOnLarge find_filterSheet__menuBackButton js_closeDetailFilterSheet js_filterSheetAction js_trackFilterMethod"
      variant="primary"
      form={formId}
      data-filter-id={filterId}
      data-ts-feature-filter-method="adopt_down"
      data-ts-labels={JSON.stringify({
        san_FilterFoldoutType: filterId,
        san_FilterFoldoutActivity: "close",
        san_FacetChangeNothing: "adopt_down",
      })}
      type="submit"
      oc-aria-label="Weiter filtern">Weiter filtern</oc-button-v1
    >
  </footer>

  {#if nextBestFilterButtons}
    <div class="find_filter__nextBestFilterButtons">
      {#if selected}
        <oc-button-v1
          class="heureka_menu-bar-button hideOnLarge ts_heureka_deselectForm"
          variant="secondary"
          form={formId}
          data-filter-id={filterId}
          data-ts-feature-filter-method="x_all_local"
          data-ts-labels={JSON.stringify({
            san_FilterFoldoutType: filterId,
            san_FilterFoldoutActivity: "close_nbf",
            san_FacetChangeNothing: "nbf",
          })}
          type="button"
          oc-aria-label="Filter Aufheben">Filter aufheben</oc-button-v1
        >
      {/if}
      <oc-button-v1
        class="heureka_menu-bar-button hideOnLarge find_filter__select js_closeFilterSheet js_trackFilterMethod"
        variant="primary"
        form={formId}
        type="button"
        oc-aria-label={selected ? "Anwenden" : "Produkte ansehen"}
        >{selected ? "Anwenden" : "Produkte ansehen"}</oc-button-v1
      >
    </div>
  {/if}
</form>
